import { useEffect, useState } from 'react';

import {
  FileReportSendResponseViewModel,
  FileReportUploadResponse,
  ReportingReceiptViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Panel, PanelStatus, QuestionsAndAnswersType } from '@foradevelopment/fora-ui';
import { fileReportMatomo, fileUploadMatomo, matomoTracking } from '@fora-monorepo/webapp-analytics';

import { FileReportingReviewAndConfirmPanel } from './FileReportingReviewAndConfirmPanel';
import { FileReportingReceiptPanel } from './FileReportingReceiptPanel';
import { FileReportingUploadPanel } from './FileReportingUploadPanel';
import { layoutSizeSettings } from '../Layout/SectionLayout';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { goToReceiptPanel, resetFileState } from '../../redux/file/fileSlice';

interface FileReportingViewProps {
  devLogging?: boolean;
  fileResponse: FileReportUploadResponse | null;
  fileUploadErrors?: string[];
  receiptResponse: FileReportSendResponseViewModel | null;
  faqsUploadStep: QuestionsAndAnswersType[];
  faqsReviewAndConfirmStep: QuestionsAndAnswersType[];
  faqsReceiptStep: QuestionsAndAnswersType[];
  sendFile: (file: File | null) => void;
  sendConfirmation: () => void;
  loading: boolean;
  getReportSummaryModalData: (index: number) => Promise<ReportingReceiptViewModel>;
  downloadPayrollDetailsCSV: (
    fileReportUploadUid: string,
    reportIndex: number,
    employerName: string,
    organizationNumber: string
  ) => Promise<string | null>;

  reloadReportData: () => void;
}

export const FileReporting = ({
  devLogging,
  fileResponse,
  fileUploadErrors,
  receiptResponse,
  faqsUploadStep,
  faqsReviewAndConfirmStep,
  faqsReceiptStep,
  sendFile,
  sendConfirmation,
  loading,
  getReportSummaryModalData,
  downloadPayrollDetailsCSV,
  reloadReportData,
}: FileReportingViewProps) => {
  const dispatch = useAppDispatch();
  const { fileUploadPanelState, reviewAndConfirmPanelState, receiptPanelState } = useAppSelector(state => state.file);
  const [fileDropFileName, setFileDropFileName] = useState<string | null>(null);

  useEffect(() => {
    if (receiptResponse?.some(report => report.success)) {
      dispatch(goToReceiptPanel());

      reloadReportData();
    }
    // Only run on receiptResponse success
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, receiptResponse]);

  const resetFileReporting = () => {
    dispatch(resetFileState());
    setFileDropFileName(null);
  };

  useEffect(() => {
    if (fileUploadPanelState.status === PanelStatus.Active) {
      matomoTracking(fileReportMatomo, 'ladda upp fil');
    }
    if (reviewAndConfirmPanelState.status === PanelStatus.Active) {
      matomoTracking(fileReportMatomo, 'granska och bekräfta');
    }
    if (receiptPanelState.status === PanelStatus.Active) {
      matomoTracking(fileReportMatomo, 'kvittens');

      const fileExtension: string = fileDropFileName?.split('.').pop() || '';
      if (fileExtension.length) matomoTracking(fileUploadMatomo, undefined, fileExtension);
    }
  }, [fileUploadPanelState.status, reviewAndConfirmPanelState.status, receiptPanelState.status, fileDropFileName]);

  return (
    <Panel.List
      paddingLeft={0}
      paddingRight={0}
      marginLeft={layoutSizeSettings.removePadding}
      marginRight={layoutSizeSettings.removePadding}
    >
      <FileReportingUploadPanel
        fileResponse={fileResponse}
        fileUploadErrors={fileUploadErrors}
        loading={loading}
        sendFile={sendFile}
        faqs={faqsUploadStep}
        fileName={fileDropFileName}
        setFileName={setFileDropFileName}
      />
      <FileReportingReviewAndConfirmPanel
        devLogging={devLogging}
        fileResponse={fileResponse}
        loading={loading}
        receiptResponse={receiptResponse}
        sendConfirmation={sendConfirmation}
        faqs={faqsReviewAndConfirmStep}
      />
      <FileReportingReceiptPanel
        devLogging={devLogging}
        faqs={faqsReceiptStep}
        fileResponse={fileResponse}
        receiptResponse={receiptResponse}
        getReportSummaryModalData={getReportSummaryModalData}
        downloadPayrollDetailsCSV={downloadPayrollDetailsCSV}
        resetFileReporting={resetFileReporting}
      />
    </Panel.List>
  );
};
